import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useSortedPaginated, SortableTh, Pagination } from '../components/TableControls';
import { formatMonthYear } from '../utils/dateUtils';
import useAutoSelectSingle from '../hooks/useAutoSelectSingle';

export default function CountingSessions() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [rawSessions, setRawSessions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ store_id: '', year: new Date().getFullYear(), month: new Date().getMonth() + 1 });
  const [msg, setMsg] = useState('');

  const sessions = rawSessions.map(s => ({ ...s, entry_count: s.entries.length }));
  const { paginated, sortKey, sortDir, setSort, page, setPage, perPage, setPerPage, total } = useSortedPaginated(sessions, 'month_year', 'desc');

  const load = () => api.get('/counting').then(r => setRawSessions(r.data));
  useEffect(() => {
    api.get('/stores').then(r => setStores(r.data));
    load();
  }, []);

  useAutoSelectSingle(stores, form.store_id, (id) => setForm(f => ({ ...f, store_id: id })));

  const create = async (e) => {
    e.preventDefault();
    setMsg('');
    try {
      const month_year = `${form.year}-${String(form.month).padStart(2, '0')}`;
      const res = await api.post('/counting', {
        store_id: parseInt(form.store_id),
        month_year,
      });
      setShowModal(false);
      load();
      navigate(`/counting/${res.data.id}`);
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Error');
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`¿Eliminar sesión de conteo de ${s.store_name} - ${s.month_year}?`)) return;
    setMsg('');
    try {
      const res = await api.delete(`/counting/${s.id}`);
      setMsg(res.data.message);
      load();
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Error');
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>Sesiones de Conteo</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>Nueva Sesión</button>
      </div>

      {msg && <div className={`alert ${msg.includes('deleted') ? 'alert-success' : 'alert-danger'}`}>{msg}</div>}

      <div className="card">
        <Pagination page={page} perPage={perPage} total={total} onPage={setPage} onPerPage={setPerPage} />
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <SortableTh colKey="store_name" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Tienda</SortableTh>
                <SortableTh colKey="month_year" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Mes</SortableTh>
                <SortableTh colKey="entry_count" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Entradas</SortableTh>
                <SortableTh colKey="created_by_name" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Por</SortableTh>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {sessions.length === 0 && <tr><td colSpan={5}>Sin sesiones de conteo aún</td></tr>}
              {paginated.map(s => (
                <tr key={s.id}>
                  <td>{s.store_name}</td>
                  <td>{formatMonthYear(s.month_year)}</td>
                  <td>{s.entry_count}</td>
                  <td>{s.created_by_name || '—'}</td>
                  <td>
                    <button className="btn btn-sm btn-primary" onClick={() => navigate(`/counting/${s.id}`)}>
                      {s.month_finalized ? 'Ver' : 'Abrir'}
                    </button>
                    {user?.role === 'admin' && (
                      <>
                        {' '}
                        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(s)}>Eliminar</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} perPage={perPage} total={total} onPage={setPage} onPerPage={setPerPage} />
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Nueva Sesión de Conteo</h2>
            {msg && <div className="alert alert-danger">{msg}</div>}
            <form onSubmit={create}>
              <div className="form-group">
                <label>Tienda</label>
                <select value={form.store_id} onChange={e => setForm({...form, store_id: e.target.value})} required>
                  <option value="">Seleccionar tienda</option>
                  {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Año</label>
                <input type="number" value={form.year} onChange={e => setForm({...form, year: parseInt(e.target.value)})} required min="2000" max="2100" />
              </div>
              <div className="form-group">
                <label>Mes</label>
                <select value={form.month} onChange={e => setForm({...form, month: parseInt(e.target.value)})} required>
                  {['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'].map((name, i) => (
                    <option key={i + 1} value={i + 1}>{name}</option>
                  ))}
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Crear</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
